import { Component, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { Button } from '../button/button';
import { ThemeService } from '../services/theme';
import { MobileModalService } from '../services/mobile-modal';
import { Subscription } from 'rxjs';
import { RouterLink } from '@angular/router';
import { SnowplowService } from '../services/snowplow';
import { makeEvent } from '../analytics/builder';
import { ModalOverlayService } from '../services/modal-overlay';
import { BreakpointObserver } from '@angular/cdk/layout';

const ASSET_STORAGE_KEY_V2 = 'explanation_modal_asset_v2_v0';
const MAIN_STORAGE_KEY_V2 = 'explanation_modal_main_v2_v0';
const ASSET_STEP_KEY = 'explanation_modal_asset_step';
const MAIN_STEP_KEY = 'explanation_modal_main_step';

@Component({
  selector: 'app-mobile-welcome-modal',
  standalone: true,
  imports: [Button, RouterLink],
  templateUrl: './mobile-welcome-modal.html',
  styleUrls: ['./mobile-welcome-modal.scss'],
  host: { class: 'v2' },
})
export class MobileWelcomeModal implements OnInit, OnDestroy {
  @Input() isAsset = false;
  visible = false;
  private sub?: Subscription;
  private breakpointSub?: Subscription;
  public wave = '/wave.png';
  public step = signal(0);
  private hasTrackedView = false;

  private readonly themeService = inject(ThemeService);
  private readonly modalService = inject(MobileModalService);
  private readonly snowplow = inject(SnowplowService);
  private readonly modalOverlay = inject(ModalOverlayService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly isSmallScreen = signal(false);

  get isLight() {
    return this.themeService.isLight();
  }

  get isFinnet() {
    return this.themeService.isFinnet();
  }

  get v2Logo() {
    return this.isSmallScreen() ? '/welcomeV2_small.png' : '/welcomeV2.png';
  }

  readonly closeIcon = '/close-light.svg';

  public stepsV2 = [
    {
      title: 'Neu: Aktienanalysen, die Du sofort verstehst.',
      subtitle:
        'Egal ob Bewertungen von Analysten, Quartalszahlen, News oder Wettbewerbsvergleiche. Die neuen Analysen von finanzen.net bringen alles für dich auf den Punkt, damit du bessere Anlageentscheidungen triffst.',
      logo: '/welcomeAsset.png',
      isDisclaimer: false,
    },
    {
      title: 'Wichtige Hinweise',
      subtitle: '',
      logo: '/welcomeAsset.png',
      isDisclaimer: true,
    },
  ];

  public stepsV2Zero = [
    {
      title: 'Informationen zu den Analysen',
      subtitle:
        'Mit Klick auf „AI-Analysen“ verlassen Sie die Anwendung der finanzen.net zero GmbH. Die AI-Analysen werden von der finanzen.net GmbH bereitgestellt und angezeigt; es gelten deren Nutzungs- und Datenschutzbestimmungen. Die finanzen.net zero GmbH stellt diese Inhalte nicht selbst bereit und übernimmt keine Verantwortung für deren Inhalt.',
      logo: '/welcomeAsset.png',
      isDisclaimer: false,
    },
    {
      title: 'Wichtige Hinweise',
      subtitle: '',
      logo: '/welcomeAsset.png',
      isDisclaimer: true,
    },
  ];

  get stepsToShow() {
    return this.isFinnet ? this.stepsV2 : this.stepsV2Zero;
  }

  get stepKey() {
    return this.isAsset ? ASSET_STEP_KEY : MAIN_STEP_KEY;
  }

  get correctScreenName() {
    return this.isAsset ? 'asset_launch' : 'main_launch';
  }

  private readonly clearStepOnExit = () => {
    localStorage.removeItem(this.stepKey);
  };

  public formatText(text: string): string {
    return text
      .replaceAll(/\*\*(.*?)\*\*/g, '<strong class="bold">$1</strong>')
      .replaceAll('\n', '<br>');
  }

  ngOnInit(): void {
    const storageKey = this.isAsset ? ASSET_STORAGE_KEY_V2 : MAIN_STORAGE_KEY_V2;
    this.modalService.init(storageKey);

    this.breakpointSub = this.breakpointObserver
      .observe(['(max-width: 430px)'])
      .subscribe((result) => this.isSmallScreen.set(result.matches));

    // Restore step
    const saved = localStorage.getItem(this.stepKey);
    this.step.set(saved ? Number(saved) : 0);

    // Clear step when user leaves the app
    window.addEventListener('beforeunload', this.clearStepOnExit);

    this.sub = this.modalService.visible$.subscribe(async (v) => {
      this.visible = v;

      if (v && !this.hasTrackedView) {
        this.hasTrackedView = true;
        this.modalOverlay.setBlockingOverlay(true);

        await this.snowplow.trackerReady();

        this.snowplow.track(
          makeEvent('element_viewed', {
            element_title: 'launch_info_screen',
            feature_name: 'page',
            property: {
              screen_step: 1,
            },
          }),
        );
      }
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.clearStepOnExit);
    this.sub?.unsubscribe();
    this.breakpointSub?.unsubscribe();
  }

  closeIconAction() {
    this.modalService.dismissForever();
    this.modalOverlay.setBlockingOverlay(false);
  }

  public incrementStep() {
    if (this.step() >= this.stepsToShow.length - 1) {
      this.closeIconAction();
      localStorage.removeItem(this.stepKey);
      return;
    }

    this.step.set(this.step() + 1);
    localStorage.setItem(this.stepKey, String(this.step()));

    this.snowplow.track(
      makeEvent('element_viewed', {
        element_title: 'launch_info_screen',
        feature_name: 'page',
        property: {
          screen_step: 2,
        },
      }),
    );
  }
}
